import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../../Layouts/Navigation/Navigation';
import { BASE_URL } from '../../../utils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faCalendar, faChartBar, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function ShipmentReport() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [stats, setStats] = useState({
    total_paquetes: 0,
    paquetes_entregados: 0,
    paquetes_pendientes: 0
  });
  const [towerStats, setTowerStats] = useState([]);
  const [towers, setTowers] = useState([]);

  const fetchStats = async () => {
    try {
      const [statsResponse, towerResponse, towersResponse] = await Promise.all([
        axios.get(`${BASE_URL}/packageStatsByDateRange?startDate=${startDate}&endDate=${endDate}`),
        axios.get(`${BASE_URL}/packagesByTowerStats?startDate=${startDate}&endDate=${endDate}`),
        axios.get(`${BASE_URL}/towers`)
      ]);
      
      setStats(statsResponse.data);
      setTowerStats(towerResponse.data);
      setTowers(towersResponse.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [startDate, endDate]);

  // Datos para el gráfico de pastel (Entregados vs Pendientes)
  const pieData = [
    { name: 'Entregados', value: stats.paquetes_entregados, color: '#10b981' },
    { name: 'Pendientes', value: stats.paquetes_pendientes, color: '#f59e0b' }
  ];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-center mb-10">
            <FontAwesomeIcon icon={faBox} className="h-10 w-10 text-primary-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-800">
              Reporte de Paquetería
            </h1>
          </div>

          {/* Selector de Fechas */}
          <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faCalendar} className="h-6 w-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-800">Rango de Fechas</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha Inicio
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha Fin
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Estadísticas Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-1">Total Paquetes</p>
                  <p className="text-4xl font-bold">{stats.total_paquetes}</p>
                </div>
                <FontAwesomeIcon icon={faBox} className="h-16 w-16 text-blue-300 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium mb-1">Entregados</p>
                  <p className="text-4xl font-bold">{stats.paquetes_entregados}</p>
                </div>
                <FontAwesomeIcon icon={faBox} className="h-16 w-16 text-green-300 opacity-50" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium mb-1">Pendientes</p>
                  <p className="text-4xl font-bold">{stats.paquetes_pendientes}</p>
                </div>
                <FontAwesomeIcon icon={faBox} className="h-16 w-16 text-orange-300 opacity-50" />
              </div>
            </div>
          </div>

          {/* Gráficas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Gráfico de Pastel - Estado de Paquetes */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faChartBar} className="h-6 w-6 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-800">Estado de Paquetes</h2>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Gráfico de Barras - Paquetes por Torre */}
            <div className="bg-white rounded-2xl shadow-md p-6">
              <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faChartBar} className="h-6 w-6 text-primary-600" />
                <h2 className="text-xl font-semibold text-gray-800">Paquetes por Torre</h2>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={towerStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="torre_nombre" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total_paquetes" fill="#297B31" name="Total Paquetes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Cards de Torres */}
          <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
            <div className="flex items-center gap-2 mb-6">
              <FontAwesomeIcon icon={faBuilding} className="h-6 w-6 text-primary-600" />
              <h2 className="text-xl font-semibold text-gray-800">Reportes por Torre</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {towers.map((tower) => (
                <div
                  key={tower.id_torre}
                  onClick={() => navigate(`/towerShipmentReport/${tower.id_torre}`)}
                  className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-md p-4 cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200 text-white"
                >
                  <FontAwesomeIcon icon={faBuilding} className="h-8 w-8 mb-2 mx-auto" />
                  <p className="text-center font-semibold text-sm">{tower.nombre}</p>
                  <p className="text-center text-xs opacity-80 mt-1">Ver reporte</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mensaje si no hay datos */}
          {stats.total_paquetes === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md">
              <FontAwesomeIcon icon={faBox} className="h-24 w-24 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">No hay paquetes en el rango seleccionado</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ShipmentReport;
